import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Tooltip,
  IconButton,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemButton,
  Avatar,
  Grid,
  Divider,
  AppBar,
  Toolbar,
  TextField,
  InputAdornment,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Paper,
  Stack,
  LinearProgress,
  Fade,
  Zoom
} from '@mui/material';
import {
  Pets,
  Event,
  MedicalServices,
  Receipt,
  Add,
  Refresh,
  Notifications,
  Assessment,
  Schedule,
  Search,
  AccountCircle,
  Brightness4,
  Brightness7,
  TrendingUp,
  TrendingDown,
  CalendarToday,
  AttachMoney,
  FileUpload,
  Message,
  ContactPhone,
  CheckCircle,
  Cancel,
  Warning,
  FilterList,
  MoreVert,
  Settings,
  Logout,
  Home,
  History,
  LocalHospital,
  Vaccines,
  Description,
  Timeline,
  Star,
  Star as StarIcon,
  Info
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { useDynamicTheme } from '@contexts/DynamicThemeProvider';
import clientDashboardService from '@services/clientDashboardService';
import CalendarMiniView from '@shared/CalendarMiniView';
import KPICard from '@shared/KPICard';
import EmptyState from '@shared/EmptyState';
import ThemeToggle from '@shared/ThemeToggle';
import { formatForDisplay, formatDateOnly, formatTimeOnly } from '@utils/dateFormatter';

interface DashboardStats {
  totalAnimals: number;
  upcomingAppointments: number;
  totalVisits: number;
  totalSpent: number;
}

interface StatCard {
  id: string;
  title: string;
  value: string | number;
  subtitle: string;
  icon: React.ReactNode;
  color: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  onClick: () => void;
}

interface QuickAction {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  onClick: () => void;
}

interface Recommendation {
  id: string;
  title: string;
  description: string;
  type: 'vaccine' | 'deworming' | 'checkup' | 'other';
  priority: 'high' | 'medium' | 'low';
  actionText: string;
  actionUrl: string;
  icon: React.ReactNode;
}

const ClientDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { mode, toggleTheme } = useDynamicTheme();

  // State pentru datele dashboard-ului
  const [appointments, setAppointments] = useState<any[]>([]);
  const [pendingAppointments, setPendingAppointments] = useState<any[]>([]);
  const [patients, setPatients] = useState<any[]>([]);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [stats, setStats] = useState<DashboardStats>({
    totalAnimals: 0,
    upcomingAppointments: 0,
    totalVisits: 0,
    totalSpent: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State pentru UI
  const [searchQuery, setSearchQuery] = useState('');
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);
  const [notificationsMenuAnchor, setNotificationsMenuAnchor] = useState<null | HTMLElement>(null);
  const [calendarFilter, setCalendarFilter] = useState<'all' | 'confirmed' | 'urgent' | 'completed'>('all');
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Încarcă statistici, toate programările viitoare, programări în așteptare, animale și notificări în paralel
      const [statsResponse, appointmentsResponse, pendingResponse, patientsResponse, notificationsResponse] = await Promise.all([
        clientDashboardService.getDashboardStats(),
        clientDashboardService.getAppointments({
          // Nu filtram dupa status pentru a obtine toate programarile viitoare
          from: new Date().toISOString().split('T')[0],
          page: 1,
          limit: 10
        }),
        clientDashboardService.getAppointments({
          status: 'pending_confirmation',
          page: 1,
          limit: 10
        }),
        clientDashboardService.getPatients(),
        clientDashboardService.getNotifications()
      ]);

      setStats(statsResponse);

      // Filtram ?i sortam programarile viitoare (excluz�nd cele cancelled sau completed)
      const futureAppointments = (appointmentsResponse.data || [])
        .filter((apt: any) => !['cancelled', 'completed', 'no_show'].includes(apt.status))
        .sort((a: any, b: any) => new Date(a.start_ts).getTime() - new Date(b.start_ts).getTime())
        .map((apt: any) => ({
          ...apt,
          // Transformam pentru CalendarMiniView
          date: apt.start_ts ? new Date(apt.start_ts).toISOString().split('T')[0] : null,
          scheduledAt: apt.start_ts,
          appointmentDate: apt.start_ts ? new Date(apt.start_ts).toISOString().split('T')[0] : null,
          // Adăugăm proprietăți pentru compatibilitate
          patient_name: apt.patient?.name || 'Animal necunoscut',
          doctor_name: apt.doctor?.name || 'Medic necunoscut',
          clinic_name: apt.clinic?.name || 'Clinica necunoscuta'
        }));

      setAppointments(futureAppointments);
      setPendingAppointments(pendingResponse.data || []);
      setPatients(patientsResponse || []);
      const recentNotifications = (notificationsResponse || []).slice(0, 5);
      setNotifications(recentNotifications);
      
      const unread = recentNotifications.filter((n: any) => !n.read_at).length;
      setUnreadNotificationsCount(unread);
      
    } catch (err) {
      console.error('❌ [ClientDashboard] Error loading dashboard data:', err);
      setError('Eroare la încărcarea datelor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadDashboardData();
    }
  }, [user]);

  const handleDateClick = (date: Date) => {
    // Aici poti naviga la pagina de programari cu data selectata
    navigate('/appointments', { state: { selectedDate: date } });
  };

  const handleAddAppointment = () => {
    navigate('/appointments');
  };

  // Funcții pentru gestionarea meniurilor
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleNotificationsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsMenuAnchor(event.currentTarget);
  };

  const handleNotificationsMenuClose = () => {
    setNotificationsMenuAnchor(null);
  };

  // Funcție pentru căutare
  const handleSearch = (query: string) => {
    if (!query.trim()) return;
    
    // Cautare �n animale
    const animalResults = patients.filter(patient => 
      patient.name?.toLowerCase().includes(query.toLowerCase()) ||
      patient.species?.toLowerCase().includes(query.toLowerCase()) ||
      patient.breed?.toLowerCase().includes(query.toLowerCase())
    );

    // Cautare �n programari
    const appointmentResults = appointments.filter(appointment =>
      appointment.patient_name?.toLowerCase().includes(query.toLowerCase()) ||
      appointment.doctor_name?.toLowerCase().includes(query.toLowerCase()) ||
      appointment.clinic_name?.toLowerCase().includes(query.toLowerCase())
    );

    // Dacă găsim rezultate, navigăm către pagina corespunzătoare
    if (animalResults.length > 0) {
      navigate('/animals', { state: { searchQuery: query } });
    } else if (appointmentResults.length > 0) {
      navigate('/appointments', { state: { searchQuery: query } });
    } else {
      navigate('/appointments', { state: { searchQuery: query, noResults: true } });
    }
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    handleSearch(searchQuery);
  };

  // Funcție pentru generarea sugestiilor de căutare
  const generateSearchSuggestions = (query: string) => {
    if (!query.trim()) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const suggestions: string[] = [];
    
    // Sugestii din animale
    patients.forEach(patient => {
      if (patient.name?.toLowerCase().includes(query.toLowerCase())) {
        suggestions.push(`Animal: ${patient.name}`);
      }
      if (patient.species?.toLowerCase().includes(query.toLowerCase())) {
        suggestions.push(`Specie: ${patient.species}`);
      }
      if (patient.breed?.toLowerCase().includes(query.toLowerCase())) {
        suggestions.push(`Rasa: ${patient.breed}`);
      }
    });

    // Sugestii din programari
    appointments.forEach(appointment => {
      if (appointment.patient_name?.toLowerCase().includes(query.toLowerCase())) {
        suggestions.push(`Programare: ${appointment.patient_name}`);
      }
      if (appointment.doctor_name?.toLowerCase().includes(query.toLowerCase())) {
        suggestions.push(`Medic: ${appointment.doctor_name}`);
      }
      if (appointment.clinic_name?.toLowerCase().includes(query.toLowerCase())) {
        suggestions.push(`Clinica: ${appointment.clinic_name}`);
      }
    });

    // Sugestii generale
    const generalSuggestions = [
      'Animalele mele',
      'Programari',
      'Vizite medicale',
      'Analize',
      'Vaccinuri',
      'Deparazitare'
    ];

    generalSuggestions.forEach(suggestion => {
      if (suggestion.toLowerCase().includes(query.toLowerCase())) {
        suggestions.push(suggestion);
      }
    });

    setSearchSuggestions(suggestions.slice(0, 5)); // Maxim 5 sugestii
    setShowSuggestions(suggestions.length > 0);
  };

  const handleSearchInputChange = (value: string) => {
    setSearchQuery(value);
    generateSearchSuggestions(value);
  };

  // Date pentru cardurile statistice
  const statCards: StatCard[] = [
    {
      id: 'animals',
      title: 'Animalele mele',
      value: stats.totalAnimals,
      subtitle: 'animale �nregistrate',
      icon: <Pets sx={{ fontSize: 22 }} />,
      color: '#4CAF50',
      trend: { value: 12, isPositive: true },
      onClick: () => navigate('/animals')
    },
    {
      id: 'appointments',
      title: 'Programari',
      value: stats.upcomingAppointments,
      subtitle: 'viitoare / ' + pendingAppointments.length + ' �n a?teptare',
      icon: <Event sx={{ fontSize: 28 }} />,
      color: '#2196F3',
      trend: { value: 8, isPositive: true },
      onClick: () => navigate('/appointments')
    },
    {
      id: 'visits',
      title: 'Istoric consultatii',
      value: stats.totalVisits,
      subtitle: 'consultatii finalizate disponibile in istoric',
      icon: <Assessment sx={{ fontSize: 28 }} />,
      color: '#FF9800',
      trend: { value: 5, isPositive: false },
      onClick: () => navigate('/appointments')
    },
    {
      id: 'expenses',
      title: 'Cheltuieli',
      value: `${stats.totalSpent} RON`,
      subtitle: 'total plati inregistrate',
      icon: <AttachMoney sx={{ fontSize: 28 }} />,
      color: '#9C27B0',
      trend: { value: 15, isPositive: true },
      onClick: () => navigate('/invoices')
    }
  ];

  // Acțiuni rapide
  const quickActions: QuickAction[] = [
    {
      id: 'view-animals',
      title: 'Animalele mele',
      description: 'Vezi animalele asociate contului tau',
      icon: <Pets sx={{ fontSize: 24 }} />,
      color: '#4CAF50',
      onClick: () => navigate('/animals')
    },
    {
      id: 'make-appointment',
      title: 'Fă o programare',
      description: 'Alege clinica si trimite o cerere de programare',
      icon: <Schedule sx={{ fontSize: 24 }} />,
      color: '#2196F3',
      onClick: () => navigate('/client/clinics')
    },
    {
      id: 'view-documents',
      title: 'Documente',
      description: 'Vezi documentele disponibile pentru contul tau',
      icon: <FileUpload sx={{ fontSize: 24 }} />,
      color: '#FF9800',
      onClick: () => navigate('/documents')
    },
    {
      id: 'send-message',
      title: 'Trimite mesaj',
      description: 'Contacteaza clinica prin mesaj',
      icon: <Message sx={{ fontSize: 24 }} />,
      color: '#9C27B0',
      onClick: () => navigate('/messages')
    },
    {
      id: 'view-invoices',
      title: 'Facturile mele',
      description: 'Vezi facturile si platile disponibile',
      icon: <Receipt sx={{ fontSize: 24 }} />,
      color: '#F44336',
      onClick: () => navigate('/invoices')
    }
  ];

  const recommendations: Recommendation[] = [];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Container principal */}
      <Container maxWidth="xl" sx={{ py: 2, px: { xs: 2, md: 3 } }}>
        {/* Secțiunea "Bine ai revenit" */}
        <Fade in timeout={600}>
          <Card 
            elevation={0}
            sx={{
              mb: 2,
              borderRadius: 2,
              overflow: 'hidden',
              position: 'relative',
              // albastru cald, pastel
              background: 'linear-gradient(135deg, #6AAFE6 0%, #A8C8F0 100%)',
              color: '#FFFFFF',
              // overlay vizual, dar NU blocheaza click-urile
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(135deg, rgba(106,175,230,0.85) 0%, rgba(168,200,240,0.85) 100%)',
                pointerEvents: 'none',   // nu blocheaza click-ul pe buton
                zIndex: 1
              }
            }}
          >
            <CardContent sx={{ p: 2, position: 'relative', zIndex: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.75, color: '#FFFFFF', fontSize: '1.25rem' }}>
                    Bine ai revenit, {user?.firstName || 'Utilizator'}! 👋
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.95, mb: 1.5, color: '#FFFFFF', fontWeight: 500, fontSize: '0.875rem' }}>
                    {patients.length > 0 
                      ? `${patients[0]?.name || 'Animalul tău'} te a?teapta la urmatoarea vizita programata pe ${appointments[0] ? formatDateOnly(appointments[0].start_ts) : 'data viitoare'}.`
                      : 'Adauga primul animal pentru a �ncepe sa folose?ti serviciile noastre.'
                    }
                  </Typography>
                  <Button
                    variant="contained"
                    sx={{ 
                      backgroundColor: 'rgba(255,255,255,0.25)',
                      color: '#fff',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: '12px',
                      px: 3,
                      py: 1.5,
                      fontSize: '0.95rem',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      '&:hover': { 
                        backgroundColor: 'rgba(255,255,255,0.35)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        transform: 'translateY(-1px)'
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                    onClick={() => navigate('/animals')}
                  >
                    Vezi animalele mele
                  </Button>
                </Box>
                
                {/* Avatar animal */}
                <Box sx={{ ml: 2 }}>
                  <Avatar 
                    sx={{ 
                      width: 56, 
                      height: 56, 
                      bgcolor: 'rgba(255,255,255,0.2)',
                      fontSize: '1.5rem'
                    }}
                  >
                    {patients.length > 0 ? '��' : '��'}
                  </Avatar>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Fade>

        {/* Carduri statistice */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {statCards.map((card, index) => (
            <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={card.id}>
              <Tooltip title={`Click pentru detalii despre ${card.title.toLowerCase()}`} arrow>
                <Zoom in timeout={600 + index * 100}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      borderRadius: 1.5,
                      border: 1,
                      borderColor: 'divider',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                        borderColor: card.color
                      }
                    }}
                    onClick={card.onClick}
                  >
            <CardContent sx={{ p: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ 
                        p: 1, 
                        borderRadius: 1.5, 
                        bgcolor: `${card.color}15`,
                        color: card.color,
                        '& svg': { fontSize: '1.25rem' }
                      }}>
                        {card.icon}
                      </Box>
                      {card.trend && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {card.trend.isPositive ? (
                            <TrendingUp sx={{ fontSize: 16, color: 'success.main' }} />
                          ) : (
                            <TrendingDown sx={{ fontSize: 16, color: 'error.main' }} />
                          )}
                          <Typography variant="caption" color={card.trend.isPositive ? 'success.main' : 'error.main'}>
                            +{card.trend.value}%
                    </Typography>
                  </Box>
                      )}
                </Box>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 700, 
                      mb: 0.25,
                      fontSize: '1.75rem',
                      color: 'text.primary'
                    }}>
                      {card.value}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: 'text.secondary',
                      fontWeight: 500,
                      mb: 0.5,
                      fontSize: '0.813rem'
                    }}>
                      {card.title}
                    </Typography>
                    
                    <Box sx={{ 
                      height: 2, 
                      bgcolor: `${card.color}20`, 
                      borderRadius: 1,
                      mb: 0.5
                    }} />
                    
                    <Typography variant="caption" sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.75rem'
                    }}>
                      {card.subtitle}
                    </Typography>
            </CardContent>
          </Card>
              </Zoom>
              </Tooltip>
        </Grid>
          ))}
      </Grid>

        {/* Layout pe doua coloane */}
        <Grid container spacing={3}>
          {/* Coloana st�nga */}
          <Grid size={{ xs: 12, lg: 7 }}>
            {/* Calendar programari */}
            <Card elevation={0} sx={{ mb: 3, borderRadius: 3, border: 1, borderColor: 'divider' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '1rem' }}>
                    Programarile tale
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {['all', 'confirmed', 'urgent', 'completed'].map((filter) => (
                      <Chip
                        key={filter}
                        label={
                          filter === 'all' ? 'Toate' :
                          filter === 'confirmed' ? 'Confirmate' :
                          filter === 'urgent' ? 'Urgente' : 'Finalizate'
                        }
                        variant={calendarFilter === filter ? 'filled' : 'outlined'}
                        onClick={() => setCalendarFilter(filter as any)}
                        size="small"
                      />
                    ))}
                  <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddAppointment}
                size="small"
                sx={{
                            bgcolor: '#5A80C8',
                            color: 'white',
                            fontWeight: 600,
                            borderRadius: 2,
                            px: 2,
                            py: 1,
                            textTransform: 'none',
                            '&:hover': {
                              bgcolor: '#4A70B8',
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 12px rgba(90, 128, 200, 0.3)'
                            },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                Programare nouă
              </Button>
              </Box>
                </Box>
                {/* calendar mai mic si centrat */}
                <Box sx={{ transform: 'scale(0.86)', transformOrigin: 'top left', width: '100%' }}>
                  <CalendarMiniView 
                    appointments={appointments}
                    onDateClick={handleDateClick}
                  />
                </Box>
              </CardContent>
            </Card>

            {/* Acțiuni rapide */}
            <Card elevation={0} sx={{ borderRadius: 1.5, border: 1, borderColor: 'divider' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontSize: '1rem' }}>
                  Acțiuni rapide
                </Typography>
                <Grid container spacing={2}>
                  {quickActions.map((action, index) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={action.id}>
                      <Tooltip title={action.description} arrow>
                        <Card 
                          elevation={0}
                          sx={{ 
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            borderRadius: 2,
                            border: 1,
                            borderColor: 'divider',
                            '&:hover': { 
                              transform: 'scale(1.03)',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                              borderColor: action.color
                            }
                          }}
                          onClick={action.onClick}
                        >
                          <CardContent sx={{ p: 2, textAlign: 'center' }}>
                            <Box sx={{ 
                              mb: 1, 
                              color: action.color,
                              display: 'flex',
                              justifyContent: 'center'
                            }}>
                              {action.icon}
                        </Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {action.title}
                            </Typography>
                    </CardContent>
                  </Card>
                      </Tooltip>
                    </Grid>
                  ))}
                </Grid>
          </CardContent>
        </Card>
        </Grid>

          {/* Coloana dreapta */}
          <Grid size={{ xs: 12, lg: 5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Facturi si plati */}
            <Card elevation={0} sx={{ mb: 2, borderRadius: 1.5, border: 1, borderColor: 'divider', flex: '1 1 auto' }}>
              <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontSize: '1rem' }}>
                  Facturi si plati
              </Typography>
                <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(90, 128, 200, 0.08)', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Total inregistrat in sistem
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#5A80C8' }}>
                    {stats.totalSpent} RON
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Pentru plata sau descarcarea documentelor financiare foloseste ecranul oficial de facturi.
                </Typography>

              <Button
                  fullWidth 
                variant="outlined"
                  sx={{ mb: 2 }}
                onClick={() => navigate('/invoices')}
                >
                  Vezi facturile
              </Button>
                
              </CardContent>
            </Card>

            {/* Recomandari personalizate */}
            <Card elevation={0} sx={{ borderRadius: 1.5, border: 1, borderColor: 'divider', flex: '1 1 auto' }}>
              <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontSize: '1rem' }}>
                  Recomandari pentru tine
                </Typography>
                
                <Alert severity="info">
                  Recomandarile automate si preventia personalizata nu au un flux owner dedicat complet in aceasta versiune.
                </Alert>
              </CardContent>
            </Card>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Meniuri */}
      {/* Meniu profil */}
      <Menu
        anchorEl={profileMenuAnchor}
        open={Boolean(profileMenuAnchor)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          sx: { minWidth: 200, borderRadius: 2 }
        }}
      >
        <MenuItem onClick={() => { navigate('/profile'); handleProfileMenuClose(); }}>
          <ListItemIcon><AccountCircle /></ListItemIcon>
          <ListItemText>Profil</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { navigate('/settings'); handleProfileMenuClose(); }}>
          <ListItemIcon><Settings /></ListItemIcon>
          <ListItemText>Setări</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { navigate('/logout'); handleProfileMenuClose(); }}>
          <ListItemIcon><Logout /></ListItemIcon>
          <ListItemText>Deconectare</ListItemText>
        </MenuItem>
      </Menu>

      {/* Meniu notificari */}
      <Menu
        anchorEl={notificationsMenuAnchor}
        open={Boolean(notificationsMenuAnchor)}
        onClose={handleNotificationsMenuClose}
        PaperProps={{
          sx: { minWidth: 300, borderRadius: 2 }
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">Notificari</Typography>
        </Box>
        {notifications.length > 0 ? (
          notifications.slice(0, 5).map((notification) => (
            <MenuItem key={notification.id} onClick={handleNotificationsMenuClose}>
              <ListItemText
                primary={notification.title}
                secondary={notification.body || notification.message}
              />
            </MenuItem>
          ))
        ) : (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Nu ai notificari noi
                </Typography>
            </Box>
        )}
      </Menu>
    </Box>
  );
};

export default ClientDashboard;
